<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  getEntities,
  EducationLevel,
  deleteEducationLevel
} from '@/app/core/services/education-levels'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const acceptQuestion = ref()
const descriptionModal = ref()

const loading = ref(false)

/* Datos */
const search = ref()
const tableEducationLevel = ref(null)
const educationLevelForDelete = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('educationLevels.list.code'), table: 'code' },
  { name: 'name', title: t('educationLevels.list.name'), table: 'name' },
  {
    name: 'updated_at',
    title: t('educationLevels.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<EducationLevel[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createEducationLevel = () => {
  void router.push({ name: 'CreateEducationLevel' })
}
const updateEducationLevel = (id: any) => {
  void router.push({ name: 'UpdateEducationLevel', params: { id } })
}

/* Eliminar nivel de educacion */
const removeEducationLevel = (values: any) => {
  showModal.value = true
  titleModal.value = t('educationLevels.list.actionStatus', {
    status: t('table.action.delete'),
    educationLevel: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  educationLevelForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteEducationLevel(educationLevelForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('educationLevels.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void educationLevels()
    })
    .catch(async (error: any) => {
      notificationError(error, 'educationLevels', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar niveles de educacion */
const educationLevels = async () => {
  loading.value = true
  try {
    const data: any = await getEntities(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'educationLevels', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deleteConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('educationLevels.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('educationLevels.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="educationLevels" />
          <DBtn
            @click.prevent="educationLevels"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createEducationLevel()"
          v-if="store.hasPermission('education_level:create')">
          {{
            t('form.button.create', {
              form: t('educationLevels.list.title', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableEducationLevel"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), educationLevels()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), educationLevels()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('education_level:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateEducationLevel(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>

          <Popper
            v-if="store.hasPermission('education_level:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeEducationLevel(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
